import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCopy, FiRefreshCw, FiHome, FiCheck, FiEdit3, FiDownload } = FiIcons;

const DescriptionOutput = ({ description, bookType, onReset, onRegenerate }) => {
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState(description);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editedDescription);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  const downloadAsText = () => {
    const element = document.createElement('a');
    const file = new Blob([editedDescription], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'book-description.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const wordCount = editedDescription.split(' ').length;
  const charCount = editedDescription.length;

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        className="bg-white rounded-2xl shadow-xl overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className={`bg-gradient-to-r ${bookType === 'fiction' ? 'from-purple-500 to-pink-600' : 'from-green-500 to-blue-600'} text-white p-6`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Your Book Description</h2>
              <p className="text-white/80">Ready for Amazon, Apple Books, and other platforms</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="text-sm text-white/80">
                {wordCount} words • {charCount} characters
              </div>
            </div>
          </div>
        </div>

        <div className="p-8">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Generated Description</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleEdit}
                  className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiEdit3} className="text-sm" />
                  <span className="text-sm">{isEditing ? 'View' : 'Edit'}</span>
                </button>
              </div>
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <textarea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <SafeIcon icon={FiCheck} />
                    <span>Save Changes</span>
                  </button>
                </div>
              </div>
            ) : (
              <motion.div
                className="bg-gray-50 rounded-lg p-6 border border-gray-200"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <pre className="whitespace-pre-wrap text-gray-700 font-sans leading-relaxed">
                  {editedDescription}
                </pre>
              </motion.div>
            )}
          </div>

          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <button
              onClick={handleCopy}
              className={`flex items-center justify-center space-x-2 px-6 py-3 rounded-lg transition-all ${
                copied 
                  ? 'bg-green-100 text-green-700 border border-green-300' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <SafeIcon icon={copied ? FiCheck : FiCopy} />
              <span>{copied ? 'Copied!' : 'Copy Text'}</span>
            </button>

            <button
              onClick={downloadAsText}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <SafeIcon icon={FiDownload} />
              <span>Download</span>
            </button>

            <button
              onClick={onRegenerate}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <SafeIcon icon={FiRefreshCw} />
              <span>Regenerate</span>
            </button>

            <button
              onClick={onReset}
              className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <SafeIcon icon={FiHome} />
              <span>Start Over</span>
            </button>
          </div>

          <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-3">Next Steps:</h4>
            <ul className="space-y-2 text-blue-700">
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <span>Copy your description and paste it into your Amazon KDP book details</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <span>Use the same description for Apple Books, Kobo, and other platforms</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <span>Consider A/B testing different versions to see what converts better</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <span>Share excerpts on social media to build interest before launch</span>
              </li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DescriptionOutput;